import { useEffect, useRef, useState } from 'react';
import { useVirtualizer } from '@tanstack/react-virtual';
import { Pencil, XCircle } from 'lucide-react';
import type { MapareRow } from '@lyra/shared';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { formatNumberRo } from '@/lib/format';
import type { WizardStepId } from '../lib/wizard';
import { validateCodMedoc, validateDenumire } from '../CorrectionPanel';
import { useBulkResolve, useResolveEntry, useRetryRows, type BulkResolution } from '../api/reviewHooks';
import { useRetryOrchestrator, isRetryBlocked } from '../RetryBar';
import { fieldAria } from '@/lib/formA11y';

export interface WizardListStepProps {
  /** Which `list`-mode step this renders (`confirmat_rezolvat` / `no_match` / `eroare` — `WIZARD_STEPS`, T1). */
  stepId: WizardStepId;
  /** Already-bucketed rows for the current step. */
  rows: MapareRow[];
  runId: number;
  /** T3c-3: true on a finalized run — every mutating control (checkboxes, primary
   *  action, per-row edit/mark-incorrect) is disabled; the server 409s writes anyway. */
  finalized: boolean;
}

/**
 * Bottom-bar label per step — the visible half of "the crux" (task brief's action
 * mapping). Pure so it's directly unit-testable without rendering.
 */
export function primaryActionLabel(stepId: WizardStepId, selectedCount: number): string {
  if (stepId === 'no_match') return `Validează ca fără corespondent (${selectedCount})`;
  if (stepId === 'eroare') return 'Reclasifică toate';
  return `Validează ${selectedCount} selectate`;
}

/** Bulk-resolve resolution for a step's primary action; `null` for `eroare` (that
 *  step retries instead of bulk-resolving — no resolution value applies). */
export function primaryActionResolution(stepId: WizardStepId): BulkResolution | null {
  if (stepId === 'eroare') return null;
  return stepId === 'no_match' ? 'no_match_confirmed' : 'accepted';
}

/** Default selection: every row in the step, keyed by id — matches v1's select-all-on-entry. */
export function defaultSelectedIds(rows: MapareRow[]): Set<number> {
  return new Set(rows.map((r) => r.id));
}

/** Threshold above which committing the step's primary action asks for confirmation
 *  first (Impeccable critique P1 — error prevention at scale). */
export const BULK_CONFIRM_THRESHOLD = 25;

/**
 * Whether the step's primary action needs an inline confirm before it commits.
 * All rows are pre-checked by `defaultSelectedIds`, so on a large step the first
 * click is the path of least resistance to committing hundreds of rows unseen —
 * above `BULK_CONFIRM_THRESHOLD` selected, the bar asks first instead of firing
 * immediately. `eroare`'s "Reclasifică toate" is exempt: it has no
 * checkbox/selection concept (every row always retries), so a count-based confirm
 * doesn't apply there — it keeps its current behavior unconditionally.
 */
export function needsBulkConfirm(stepId: WizardStepId, selectedCount: number): boolean {
  if (stepId === 'eroare') return false;
  return selectedCount >= BULK_CONFIRM_THRESHOLD;
}

/**
 * Fires the step's primary action against already-bound hook handles — split out from
 * the component so the action-mapping is unit-testable without a DOM (this repo currently
 * has no component-render test setup — see `WizardListStep.test.ts`'s header comment).
 * `confirmat_rezolvat`/`no_match` bulk-resolve the selected ids; `eroare` retries every row.
 */
export function runPrimaryAction(
  stepId: WizardStepId,
  ids: number[],
  rows: MapareRow[],
  handlers: {
    bulkResolve: (vars: { ids: number[]; resolution: BulkResolution }) => void;
    retryAll: (rows: MapareRow[]) => void | Promise<void>;
  }
): void | Promise<void> {
  const resolution = primaryActionResolution(stepId);
  if (resolution === null) return handlers.retryAll(rows);
  return handlers.bulkResolve({ ids, resolution });
}

const iconButtonCls =
  'flex size-6 shrink-0 items-center justify-center rounded text-ink-3 disabled:opacity-40 disabled:hover:text-ink-3';

/** Inline row editor (Direction B — replaces v1's `EditModal` dialog, no modal chrome).
 *  Seeded from the row's current/provisional values; validated the same way as
 *  `CorrectionPanel`'s "Editează manual" section (same helpers, reused verbatim). */
function InlineRowEditor({
  row,
  saving,
  onCancel,
  onSave,
}: {
  row: MapareRow;
  saving: boolean;
  onCancel: () => void;
  onSave: (patch: { codMedoc: string; denumireMedoc: string; codBdMedoc?: string }) => void;
}) {
  const [cod, setCod] = useState(row.codMedoc || row.provisionalCodMedoc || '');
  const [den, setDen] = useState(row.denumireMedoc || row.provisionalDenumireMedoc || '');
  const [bd, setBd] = useState(row.codBdMedoc || row.provisionalCodBdMedoc || '');
  const codErr = cod.trim() ? validateCodMedoc(cod) : null;
  const denErr = den.trim() ? validateDenumire(den) : null;
  const valid = validateCodMedoc(cod) === null && validateDenumire(den) === null;

  return (
    <div className="min-w-0 flex-1 space-y-2 py-1">
      <div className="flex flex-wrap items-start gap-2">
        <div>
          <Input
            aria-label="Cod MEDOC"
            value={cod}
            onChange={(e) => setCod(e.target.value)}
            placeholder="Cod MEDOC"
            className="h-8 w-32 font-mono"
            {...fieldAria(!!codErr, `wiz-cod-error-${row.id}`)}
          />
          {codErr && <p id={`wiz-cod-error-${row.id}`} className="mt-1 text-[12.5px] text-bad">{codErr}</p>}
        </div>
        <div className="min-w-[10rem] flex-1">
          <Input
            aria-label="Denumire MEDOC"
            value={den}
            onChange={(e) => setDen(e.target.value)}
            placeholder="Denumire MEDOC"
            className="h-8"
            {...fieldAria(!!denErr, `wiz-den-error-${row.id}`)}
          />
          {denErr && <p id={`wiz-den-error-${row.id}`} className="mt-1 text-[12.5px] text-bad">{denErr}</p>}
        </div>
        <Input
          aria-label="Cod BD MEDOC"
          value={bd}
          onChange={(e) => setBd(e.target.value)}
          placeholder="Cod BD MEDOC (opțional)"
          className="h-8 w-32 font-mono"
        />
      </div>
      <div className="flex gap-2">
        <Button
          type="button"
          size="sm"
          disabled={saving || !valid}
          onClick={() =>
            onSave({ codMedoc: cod.trim(), denumireMedoc: den.trim(), codBdMedoc: bd.trim() || undefined })
          }
        >
          Salvează
        </Button>
        <Button type="button" variant="outline" size="sm" onClick={onCancel} disabled={saving}>
          Anulează
        </Button>
      </div>
    </div>
  );
}

/**
 * `list`-mode wizard steps (Plan 3c-2's Task 4) — port of v1
 * `components/wizard/WizardListStep.tsx` (Rule Zero), retokenized to v2's `teal`/`ink`
 * tokens, and with v1's `EditModal` replaced by `InlineRowEditor` above (Direction B: no
 * modal).
 *
 * Replaces T3's read-only `WizardListPlaceholder`. Default-selects every row on entry
 * (and whenever the step/its row count changes — same `[stepId, rows.length]` dependency
 * v1 used), offers a per-row checkbox (hidden on `eroare` — that step has no bulk
 * selection, only "Reclasifică toate" over every row) and a bottom bar whose label +
 * wired action come from `primaryActionLabel`/`runPrimaryAction` above.
 *
 * R1 fix (Impeccable critique P1 — "the bulk-validate action bar is buried"): the page
 * itself still scrolls as a whole (`WizardShell`'s layout notes — the app has no internal
 * fixed-viewport clipping), but the bottom bar is now `position: sticky; bottom: 0`
 * against that scrolling ancestor (same idiom as `ResultsTable`'s `sticky top-0`
 * selection toolbar), so it stays visible from row 1 on a 281- or 807-row step instead of
 * requiring a full scroll to reach the primary action. Because every row starts
 * pre-checked, committing hundreds unseen was the path of least resistance — above
 * `BULK_CONFIRM_THRESHOLD` selected rows, the first click swaps the bar into an inline
 * confirm state (`confirming`) instead of committing immediately; a second click commits.
 * No modal — this is a state swap on the same bar, per Direction B.
 */
export function WizardListStep({ stepId, rows, runId, finalized }: WizardListStepProps) {
  const isError = stepId === 'eroare';
  const [selected, setSelected] = useState<Set<number>>(() => defaultSelectedIds(rows));
  const [editingId, setEditingId] = useState<number | null>(null);
  /** Scale-aware inline confirm (R1 fix): true once the first click on a
   *  ≥`BULK_CONFIRM_THRESHOLD`-selection commit has swapped the bar into
   *  "are you sure" mode. Reset on step change and on any selection edit
   *  (the confirm text quotes a row count — it must not go stale). */
  const [confirming, setConfirming] = useState(false);

  const bulkResolve = useBulkResolve(runId);
  const retry = useRetryRows(runId);
  // Shared retry orchestrator (same one the table + RetryBar use): bounded-concurrency,
  // per-row resilient (one row failing never aborts the batch), and it detects the
  // no-active-instance 403 → offers a „Pornește instanța" CTA in place. Replaces the
  // old bespoke sequential loop that stopped at the first failure with no start CTA.
  const orchestrator = useRetryOrchestrator(retry);
  const resolveEntry = useResolveEntry();

  // Default-select all rows when entering the step or when the bucket changes.
  useEffect(() => {
    setSelected(defaultSelectedIds(rows));
    setConfirming(false);
  }, [stepId, rows.length]);

  // The `confirmat_rezolvat` bucket is typically the LARGEST partition of a run
  // (the high-confidence auto-classified rows) — this step routinely holds
  // 800+ rows, and every checkbox tick re-renders the whole list. Rendering all
  // of them at once mounts thousands of DOM nodes and makes each toggle O(n).
  // Virtualize (same TanStack pattern as WizardReviewStep / ResultsTable) so
  // only on-screen rows mount and only they re-render on a toggle. Rows are
  // light (no per-row query), so the estimate is small; measureElement corrects
  // it (and the taller inline-editor row) in a real browser.
  const parentRef = useRef<HTMLDivElement>(null);
  const rowVirtualizer = useVirtualizer({
    count: rows.length,
    getScrollElement: () => parentRef.current,
    estimateSize: () => 52,
    getItemKey: (index) => rows[index].id,
    overscan: 8,
  });

  const ids = [...selected];

  // Delegates to the resilient orchestrator: it swallows per-row failures and
  // continues, stops the batch on the no-instance 403 (surfacing a start CTA),
  // and advances a progress counter — none of which the old inline loop did.
  const retryAll = (target: MapareRow[]) => orchestrator.runRetry(target);

  function commit() {
    void runPrimaryAction(stepId, ids, rows, {
      bulkResolve: (vars) => bulkResolve.mutate(vars),
      retryAll,
    });
    setConfirming(false);
  }

  /** Primary button click: below the confirm threshold (or already confirming, or
   *  `eroare` — exempt per `needsBulkConfirm`), commits directly (v1 behavior).
   *  Otherwise the first click only arms the inline confirm state. */
  function handlePrimary() {
    if (!confirming && needsBulkConfirm(stepId, ids.length)) {
      setConfirming(true);
      return;
    }
    commit();
  }

  function toggle(id: number, checked: boolean) {
    setSelected((s) => {
      const n = new Set(s);
      if (checked) n.add(id);
      else n.delete(id);
      return n;
    });
    setConfirming(false);
  }

  function saveEdit(row: MapareRow, patch: { codMedoc: string; denumireMedoc: string; codBdMedoc?: string }) {
    resolveEntry.mutate(
      { id: row.id, runId, patch: { ...patch, status: 'edited' } },
      { onSuccess: () => setEditingId(null) }
    );
  }

  function markIncorrect(row: MapareRow) {
    resolveEntry.mutate({ id: row.id, runId, patch: { status: 'error', accepted: false } });
  }

  if (rows.length === 0) {
    return <p className="py-10 text-center text-[13.5px] text-ink-3">Pas finalizat.</p>;
  }

  const retryBlocked = isRetryBlocked(orchestrator);
  const primaryDisabled =
    finalized ||
    (isError ? retryBlocked || rows.length === 0 : ids.length === 0 || bulkResolve.isPending);

  return (
    <div>
      <div ref={parentRef} className="mb-4 max-h-[70vh] overflow-y-auto border-t border-rule-soft">
        <div style={{ height: rowVirtualizer.getTotalSize(), position: 'relative' }}>
          {rowVirtualizer.getVirtualItems().map((virtualRow) => {
            const row = rows[virtualRow.index];
            return (
              <div
                key={row.id}
                data-index={virtualRow.index}
                ref={rowVirtualizer.measureElement}
                style={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  width: '100%',
                  transform: `translateY(${virtualRow.start}px)`,
                }}
              >
                {/* 2026-08-21 („textele mai apropiate in ecranul de
                    confirmate/rezolvate"): this step renders on the FULL-WIDTH
                    canvas, and the two strings the operator compares — the
                    partner's denumire and the MEDOC one — used to be pushed to
                    opposite screen edges by `flex-1` + `text-right`. Bounding
                    the line and centring it keeps them side by side at any
                    canvas width; both sides now read left-aligned, so the
                    arrow between them is the only thing separating the pair. */}
                <div className="mx-auto flex max-w-4xl items-center gap-3 border-b border-rule-soft py-2 text-[13.5px]">
                  {!isError && (
                    <input
                      type="checkbox"
                      checked={selected.has(row.id)}
                      onChange={(e) => toggle(row.id, e.target.checked)}
                      disabled={finalized}
                      className="size-4 shrink-0 accent-teal disabled:opacity-40"
                      aria-label={`Selectează ${row.code || row.name}`}
                    />
                  )}

                  {editingId === row.id ? (
                    <InlineRowEditor
                      row={row}
                      saving={resolveEntry.isPending}
                      onCancel={() => setEditingId(null)}
                      onSave={(patch) => saveEdit(row, patch)}
                    />
                  ) : (
                    <>
                      <div className="min-w-0 flex-1">
                        <p className="truncate font-mono text-[11.5px] text-ink-3">{row.code || '—'}</p>
                        <p className="truncate text-ink-2" title={row.name}>
                          {row.name}
                        </p>
                      </div>
                      {isError ? (
                        <span className="max-w-[16rem] shrink-0 truncate text-[12.5px] text-bad" title={row.error}>
                          {row.error || '—'}
                        </span>
                      ) : (
                        <>
                          <span className="shrink-0 text-ink-3" aria-hidden>
                            →
                          </span>
                          <div className="min-w-0 flex-1">
                            <p className="truncate font-mono text-[11.5px] text-ink-3">
                              {row.codMedoc || row.provisionalCodMedoc || '—'}
                            </p>
                            <p
                              className="truncate text-ink-2"
                              title={row.denumireMedoc || row.provisionalDenumireMedoc || ''}
                            >
                              {row.denumireMedoc || row.provisionalDenumireMedoc || '—'}
                            </p>
                          </div>
                        </>
                      )}
                      {!isError && (
                        <div className="flex shrink-0 items-center gap-1">
                          <button
                            type="button"
                            onClick={() => setEditingId(row.id)}
                            disabled={finalized}
                            title="Editează"
                            aria-label={`Editează ${row.name}`}
                            className={`${iconButtonCls} hover:text-teal`}
                          >
                            <Pencil className="size-3.5" />
                          </button>
                          <button
                            type="button"
                            onClick={() => markIncorrect(row)}
                            disabled={finalized || resolveEntry.isPending}
                            title="Marchează incorect"
                            aria-label={`Marchează incorect ${row.name}`}
                            className={`${iconButtonCls} hover:text-bad`}
                          >
                            <XCircle className="size-3.5" />
                          </button>
                        </div>
                      )}
                    </>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* R1 fix: pinned to the viewport bottom (sticky against `AppShell`'s scrolling
          ancestor, same idiom as `ResultsTable`'s `sticky top-0` toolbar) so the primary
          action stays reachable from row 1 on a long list, instead of ~15,000px down a
          807-row step. Solid `bg-bg` + top rule + a subtle upward shadow read it as
          pinned floating chrome (the one shadow Direction B sanctions). */}
      <div
        className={`sticky bottom-0 z-20 border-t bg-bg pb-4 pt-3 shadow-[0_-6px_16px_-10px_rgba(0,0,0,0.35)] ${
          confirming ? 'border-warn' : 'border-rule'
        }`}
      >
        {isError && (orchestrator.sessionErrorMessage || orchestrator.startErrorMessage) && (
          <p role="alert" className="mb-3 text-[13.5px] text-bad">
            {orchestrator.sessionErrorMessage || orchestrator.startErrorMessage}
          </p>
        )}

        {confirming ? (
          // Scale-aware inline confirm (R1 fix): armed on the first click once
          // `needsBulkConfirm` trips (≥ BULK_CONFIRM_THRESHOLD selected). No modal —
          // the bar itself swaps content, per Direction B's "no modal layer".
          <div className="flex flex-wrap items-center justify-end gap-3">
            <span className="text-[13.5px] text-ink-2">
              Validezi <span className="font-semibold tabular-nums text-ink">{formatNumberRo(ids.length)}</span> de
              rânduri?
            </span>
            <button
              type="button"
              onClick={() => setConfirming(false)}
              className="text-[13.5px] font-medium text-ink-3 hover:text-ink-2"
            >
              Anulează
            </button>
            <Button type="button" onClick={commit} disabled={finalized || bulkResolve.isPending}>
              Da, validează
            </Button>
          </div>
        ) : (
          <div className="flex flex-wrap items-center justify-end gap-3">
            {!isError && (
              <span className="tabular-nums text-[12.5px] text-ink-3">
                {formatNumberRo(ids.length)}/{formatNumberRo(rows.length)} selectate
              </span>
            )}
            {isError && orchestrator.progress && (
              <span className="tabular-nums text-[12.5px] text-ink-3">
                Se reîncearcă… {orchestrator.progress.done}/{orchestrator.progress.total}
              </span>
            )}
            {isError && (orchestrator.sessionErrorMessage || (orchestrator.sessionKnown && !orchestrator.hasActiveSession)) && (
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={orchestrator.startSession}
                disabled={orchestrator.isStarting}
              >
                {orchestrator.isStarting ? 'Se pornește…' : 'Pornește instanța'}
              </Button>
            )}
            <Button type="button" onClick={handlePrimary} disabled={primaryDisabled}>
              {primaryActionLabel(stepId, ids.length)}
            </Button>
          </div>
        )}
      </div>
    </div>
  );
}
